/**
 * Module: screens/creator/steps/DemographicsStep
 * Role: Wizard Step 6/8 — audience gender + age range, both optional (FTR-1131
 *       chrome rewrite). Russian labels via _labels.ts, enum values persisted.
 *       Single-select chips with toggle-off behaviour.
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - screens/creator/useCreatorOptions:useCreatorOptions (BUG-1144 auth-gated fetch)
 *   - screens/creator/store:useWizardStore
 *   - screens/creator/_chrome/WizardShell
 *   - screens/creator/_labels:genderLabel, ageRangeLabel
 *
 * Used by: CreatorOnboardingScreen.tsx
 * ADR-023: no window.Telegram / platform literals outside src/platform/
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreatorOptions } from '../useCreatorOptions';
import { useWizardStore } from '../store';
import { ageRangeLabel, genderLabel } from '../_labels';
import { P, T } from '../../../styles/tokens';
import {
  WizardBody, WizardCtaBar, WizardFrame, WizardHeader, WizardStepStrip,
} from '../_chrome/WizardShell';

/** Single-select chip — toggle-off when the active one is tapped again. */
function SingleChip({
  label, active, onTap,
}: {
  label: string; active: boolean; onTap(): void;
}) {
  return (
    <button
      type="button"
      onClick={onTap}
      style={{
        padding: '9px 15px',
        borderRadius: 20,
        border: `1.5px solid ${active ? P.terracotta : P.line}`,
        background: active ? P.terraSoft : P.card,
        color: active ? P.terraDark : P.ink,
        font: T.subhead,
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      {label}
    </button>
  );
}

export default function DemographicsStep() {
  const navigate = useNavigate();
  const { draft, setProfile } = useWizardStore();

  const [gender, setGender] = useState(draft.gender);
  const [ageRange, setAgeRange] = useState(draft.age_range);

  const { options, isLoading: optLoading } = useCreatorOptions();

  const genders = options?.genders ?? [];
  const ageRanges = options?.age_ranges ?? [];

  const handleBack = () => navigate(-1);

  function handleNext() {
    setProfile({ gender, age_range: ageRange });
    navigate('/creator-onboarding/cities');
  }

  return (
    <WizardFrame>
      <WizardHeader title="Верификация" onBack={handleBack} />
      <WizardStepStrip current={6} total={8} />
      <WizardBody>
        <div style={{ paddingTop: 10 }}>
          <div style={{ font: T.title2, color: P.ink, marginBottom: 4 }}>
            Аудитория
          </div>
          <div style={{ font: T.footnote, color: P.inkSoft }}>
            Кто вас смотрит — оба поля необязательны
          </div>
        </div>

        {optLoading && (
          <div style={{ color: P.inkSoft, font: T.subhead }}>Загрузка…</div>
        )}

        {!optLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div>
              <div style={{ font: T.footnote, color: P.inkSoft, marginBottom: 8 }}>
                Пол аудитории
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {genders.map((g) => (
                  <SingleChip
                    key={g}
                    label={genderLabel(g)}
                    active={gender === g}
                    onTap={() => setGender(gender === g ? '' : g)}
                  />
                ))}
              </div>
            </div>
            <div>
              <div style={{ font: T.footnote, color: P.inkSoft, marginBottom: 8 }}>
                Возраст аудитории
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {ageRanges.map((a) => (
                  <SingleChip
                    key={a}
                    label={ageRangeLabel(a)}
                    active={ageRange === a}
                    onTap={() => setAgeRange(ageRange === a ? '' : a)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </WizardBody>
      <WizardCtaBar label="Далее" onClick={handleNext} />
    </WizardFrame>
  );
}
